<script setup lang="ts">
  import { computed } from 'vue';

  import type { UserListItem } from '@stuut/api-sdk/models';
  import { UserRoleLevel } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Skeleton } from '@ui/components/skeleton';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { isUserOutOfOffice } from '@/components/out-of-office/useOutOfOfficeResponderForm';
  import { UserAvatar, UserRoleSelect } from '@/components/users';
  import { useProfile } from '@/store';

  import UserOutOfOfficeAlert from './UserOutOfOfficeAlert.vue';
  import type { UserOutOfOfficeAlertVariant } from './UserOutOfOfficeAlert.vue';

  interface Props {
    /** Whether OOO management is enabled (i.e. a shared inbox is connected). */
    isOooEnabled: boolean;
    /** Whether the shared-inbox check has finished loading. */
    isOooReady: boolean;
    /** When set, shows the out-of-office banner below the row. */
    outOfOfficeSummary?: string | null;
    /** `warning`: yellow alert. `neutral`: gray alert (e.g. backup selected). */
    outOfOfficeVariant?: UserOutOfOfficeAlertVariant;
    user: UserListItem;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    backInOffice: [user: UserListItem];
    editOutOfOffice: [user: UserListItem];
    setOutOfOffice: [user: UserListItem];
    userUpdated: [user: Pick<UserListItem, 'id'> & Partial<UserListItem>];
  }>();

  const profileStore = useProfile();

  const isOutOfOffice = computed(() => isUserOutOfOffice(props.user));

  const canManageOoo = computed(
    () =>
      props.isOooEnabled &&
      profileStore.profile?.userRole === UserRoleLevel.Admin &&
      profileStore.profile?.id !== props.user.id &&
      !props.user.isAccDisabled,
  );

  const onOooButtonClick = () => {
    if (isOutOfOffice.value) emit('editOutOfOffice', props.user);
    else emit('setOutOfOffice', props.user);
  };
</script>

<template>
  <div class="flex w-full flex-col gap-3">
    <div
      class="grid w-full grid-cols-[min-content_1fr_170px] items-center gap-x-4"
      :class="canManageOoo && 'grid-cols-[min-content_1fr_170px_min-content]!'"
    >
      <UserAvatar :user="user" />

      <div class="flex flex-col space-y-1 overflow-hidden">
        <p class="text-sm font-medium overflow-x-hidden text-ellipsis text-nowrap">
          {{ user.name }}
        </p>

        <span class="text-xs text-nowrap overflow-hidden text-ellipsis text-muted-foreground">
          {{ user.email }}
        </span>
      </div>

      <UserRoleSelect :user="user" class="ml-auto!" @user-updated="emit('userUpdated', $event)" />

      <Tooltip v-if="canManageOoo">
        <TooltipTrigger as-child>
          <Button
            variant="outline"
            size="icon-sm"
            :aria-label="isOutOfOffice ? 'Edit out of office' : 'Set out of office'"
            @click="onOooButtonClick"
          >
            <i class="far fa-calendar-xmark" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{{
          isOutOfOffice ? 'Edit out of office' : 'Set out of office'
        }}</TooltipContent>
      </Tooltip>
    </div>

    <Skeleton v-if="!isOooReady && isOutOfOffice" class="h-12 w-full rounded-xs" />
    <UserOutOfOfficeAlert
      v-else-if="isOooEnabled && isOutOfOffice"
      :user="user"
      :summary="outOfOfficeSummary"
      :variant="outOfOfficeVariant"
      @back-in-office="emit('backInOffice', $event)"
      @edit-out-of-office="emit('editOutOfOffice', $event)"
    />
  </div>
</template>

<style scoped></style>
